// Google reCAPTCHA v3 (invisible). Lazily injects the reCAPTCHA script the first
// time a token is requested, then returns a fresh token per form submission.
// The token must be verified server-side (see the /api/mailer + /api/subscribe
// backend) — the frontend only obtains it.

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// Shared across all callers so the script is only ever injected once.
let scriptPromise: Promise<void> | null = null;

export function useRecaptcha() {
  const config = useRuntimeConfig();
  const siteKey = config.public.recaptchaSiteKey as string;

  function loadRecaptcha(): Promise<void> {
    // No-op on the server, or when no key is configured.
    if (import.meta.server || !siteKey) return Promise.resolve();
    if (window.grecaptcha) return Promise.resolve();
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load reCAPTCHA'));
      };
      document.head.appendChild(script);
    });

    return scriptPromise;
  }

  // Returns a reCAPTCHA token for the given action, or '' when reCAPTCHA is
  // disabled / unavailable so submissions still go through (backend decides).
  async function executeRecaptcha(action: string): Promise<string> {
    if (import.meta.server || !siteKey) return '';

    try {
      await loadRecaptcha();
    } catch {
      return '';
    }

    return new Promise<string>((resolve) => {
      if (!window.grecaptcha) {
        resolve('');
        return;
      }
      window.grecaptcha.ready(() => {
        window.grecaptcha!.execute(siteKey, { action }).then(resolve).catch(() => resolve(''));
      });
    });
  }

  return { loadRecaptcha, executeRecaptcha };
}
